import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, itemCount, updateItem, removeItem, loading } = useCart();
  const navigate = useNavigate();

  if (!cartOpen) return null;

  const items = cart?.items || [];

  const handleProceed = () => {
    setCartOpen(false);
    navigate('/inquiry');
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-on-surface/50 z-40" onClick={() => setCartOpen(false)} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 flex flex-col shadow-2xl animate-slide-up">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 bg-on-surface text-white">
          <div>
            <h2 className="font-headline font-black text-xl uppercase tracking-tight">Inquiry List</h2>
            <p className="font-label text-xs text-gray-400 mt-0.5">{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={() => setCartOpen(false)} className="p-2 text-gray-400 hover:text-white transition-colors">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
              <span className="material-symbols-outlined text-[64px] text-outline-variant">list_alt</span>
              <p className="font-headline font-bold text-xl uppercase text-secondary">Your list is empty</p>
              <p className="font-body text-sm text-secondary">Browse products and add items to get started.</p>
              <button onClick={() => { setCartOpen(false); navigate('/products'); }} className="btn-primary text-sm">
                Browse Products
              </button>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="flex gap-4 p-4 bg-surface-container-lowest border border-outline-variant/20">
                {/* Icon (no product image available client-side anymore) */}
                <div className="w-16 h-16 bg-surface-container flex-shrink-0 overflow-hidden flex items-center justify-center">
                  <span className="material-symbols-outlined text-[28px] text-outline-variant">bolt</span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-headline font-bold text-sm uppercase tracking-tight text-on-surface truncate">{item.productName}</p>
                  {item.sizeLabel && (
                    <p className="font-label text-xs text-secondary mt-0.5">Size: {item.sizeLabel}</p>
                  )}

                  {/* Qty controls */}
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => updateItem(item.id, Math.max(1, item.quantity - 1))}
                      disabled={loading}
                      className="w-7 h-7 border border-outline-variant flex items-center justify-center text-secondary hover:border-primary-container hover:text-primary-container transition-colors disabled:opacity-50">
                      <span className="material-symbols-outlined text-[16px]">remove</span>
                    </button>
                    <span className="font-label font-bold text-sm w-8 text-center">{item.quantity}</span>
                    <button onClick={() => updateItem(item.id, item.quantity + 1)}
                      disabled={loading}
                      className="w-7 h-7 border border-outline-variant flex items-center justify-center text-secondary hover:border-primary-container hover:text-primary-container transition-colors disabled:opacity-50">
                      <span className="material-symbols-outlined text-[16px]">add</span>
                    </button>
                  </div>
                </div>

                {/* Remove */}
                <button onClick={() => removeItem(item.id)} disabled={loading}
                  className="p-1 text-outline-variant hover:text-error transition-colors self-start disabled:opacity-50">
                  <span className="material-symbols-outlined text-[18px]">delete</span>
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-surface-container space-y-3">
            <div className="flex items-center justify-between font-headline font-bold text-base uppercase">
              <span className="text-secondary">Total Items</span>
              <span className="text-primary-container text-2xl">{itemCount}</span>
            </div>
            <button onClick={handleProceed} className="btn-primary w-full justify-center text-base py-4">
              <span className="material-symbols-outlined">send</span>
              Submit Inquiry
            </button>
          </div>
        )}
      </div>
    </>
  );
}
